import { apiGet } from '@/lib/api-utils';

// ============================================================================
// Common Types
// ============================================================================

export type ReportPeriod =
  | 'today'
  | 'yesterday'
  | 'last_7_days'
  | 'last_30_days'
  | 'this_month'
  | 'last_month'
  | 'this_year'
  | 'custom'
  | 'custom_month'
  | 'custom_day';

export type ApprovalStatus = 'APPROVED' | 'PENDING' | 'DENIED';

export type ApprovalCategory =
  | 'GENERAL_APPROVAL'
  | 'LEAVE_APPROVAL'
  | 'SALARY_APPROVAL'
  | 'TRAVEL_APPROVAL'
  | 'OPERATIONAL_APPROVAL'
  | 'QUOTATION_APPROVAL'
  | 'CONTRACT_APPROVAL'
  | 'PAYMENT_APPROVAL'
  | 'OVERTIME_APPROVAL'
  | 'PUNCH_IN_APPROVAL';

// ============================================================================
// Constants
// ============================================================================

export const APPROVAL_CATEGORIES: { value: ApprovalCategory; label: string }[] = [
  { value: 'GENERAL_APPROVAL', label: 'General Expense' },
  { value: 'LEAVE_APPROVAL', label: 'Leave Request' },
  { value: 'SALARY_APPROVAL', label: 'Advance Salary' },
  { value: 'TRAVEL_APPROVAL', label: 'Travel Expense' },
  { value: 'OPERATIONAL_APPROVAL', label: 'Operational Expense' },
  { value: 'QUOTATION_APPROVAL', label: 'Quotation Approval' },
  { value: 'CONTRACT_APPROVAL', label: 'Contract Approval' },
  { value: 'PAYMENT_APPROVAL', label: 'Vendor Payment' },
  { value: 'OVERTIME_APPROVAL', label: 'Overtime Request' },
  { value: 'PUNCH_IN_APPROVAL', label: 'Punch-in Approval' },
];

export const APPROVAL_STATUSES: { value: ApprovalStatus; label: string }[] = [
  { value: 'APPROVED', label: 'Approved' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'DENIED', label: 'Denied' },
];

export const PERIOD_OPTIONS: { value: ReportPeriod; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' },
  { value: 'custom_month', label: 'Specific Month' },
  { value: 'custom_day', label: 'Specific Day' },
];

// ============================================================================
// Approval Report Types
// ============================================================================

export interface GetApprovalReportRequest {
  company_id: number;
  branch_id?: number; // -1 for all branches
  employee_id?: number; // specific employee
  period?: ReportPeriod;
  // Custom date range (for period=custom)
  start_date?: string; // Y-m-d format
  end_date?: string; // Y-m-d format
  // Custom month (for period=custom_month)
  year?: number;
  month?: number; // 1-12
  // Custom day (for period=custom_day)
  day?: number; // 1-31
  // Pagination
  page?: number;
  limit?: number; // max 100
  // Filters
  filter_category?: ApprovalCategory[];
  filter_status?: ApprovalStatus[];
  include_auto_rejected?: 0 | 1; // 1=yes (default), 0=no
  // Include options
  include?: 'all' | 'stats' | 'logs';
}

export interface ApprovalReportSummary {
  total: number;
  approved: number;
  pending: number;
  denied: number;
}

export interface ApprovalCategoryStatus {
  media_type: string;
  approved: number;
  pending: number;
  denied: number;
  total: number;
}

export interface ApprovalReportInsights {
  average_approval_time: string;
  highest_category: string;
  lowest_category: string;
  most_active_employee: string;
  percent_change_from_last_month: string;
  most_sent_by: string;
  most_approved_by: string;
}

export interface ApprovalLogStatus {
  status: string;
  isAutoRejected: boolean;
  actionNote?: string;
  actionAt?: string;
  actionBy?: string;
}

export interface ApprovalLog {
  employeeName: string;
  branchName: string;
  type: string;
  mediaType: string;
  title: string;
  details: string;
  sentAt: string;
  status: ApprovalLogStatus;
  srNo: string;
  hasAttachment: boolean;
  notes: string;
  responseTime: string;
  actionAt: string | null;
  actionBy: string;
  isAutoRejected: boolean;
  autoRejectedNotes: string;
}

export interface ApprovalReportPagination {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface ApprovalReportData {
  company: string;
  company_id: number;
  branch: string;
  branch_id: number;
  employee: string;
  employee_id: number;
  period: string;
  start_date: string;
  end_date: string;
  period_days: number;
  include: string;
  summary: ApprovalReportSummary;
  by_category: Record<string, number>;
  by_category_status: Record<string, ApprovalCategoryStatus>;
  insights: ApprovalReportInsights;
  logs: ApprovalLog[];
  logs_count: number;
  logs_pagination: ApprovalReportPagination;
}

export interface GetApprovalReportResponse {
  success: boolean;
  data: ApprovalReportData;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get approval report data
 * Uses apiGet utility for consistency with other services
 */
export const getApprovalReport = async (
  params: GetApprovalReportRequest
): Promise<GetApprovalReportResponse> => {
  const queryParams = new URLSearchParams();

  queryParams.set('company_id', params.company_id.toString());

  if (params.branch_id !== undefined && params.branch_id !== -1) {
    queryParams.set('branch_id', params.branch_id.toString());
  }

  if (params.employee_id !== undefined && params.employee_id !== -1) {
    queryParams.set('employee_id', params.employee_id.toString());
  }

  if (params.period) {
    queryParams.set('period', params.period);
  }

  // Custom date range
  if (params.period === 'custom' && params.start_date && params.end_date) {
    queryParams.set('start_date', params.start_date);
    queryParams.set('end_date', params.end_date);
  }

  // Custom month
  if (params.period === 'custom_month' && params.year && params.month) {
    queryParams.set('year', params.year.toString());
    queryParams.set('month', params.month.toString());
  }

  // Custom day
  if (params.period === 'custom_day' && params.year && params.month && params.day) {
    queryParams.set('year', params.year.toString());
    queryParams.set('month', params.month.toString());
    queryParams.set('day', params.day.toString());
  }

  // Pagination
  if (params.page !== undefined) {
    queryParams.set('page', params.page.toString());
  }

  if (params.limit !== undefined) {
    queryParams.set('limit', params.limit.toString());
  }

  // Filters
  if (params.filter_category && params.filter_category.length > 0) {
    params.filter_category.forEach(cat => {
      queryParams.append('filter_category[]', cat);
    });
  }

  if (params.filter_status && params.filter_status.length > 0) {
    params.filter_status.forEach(status => {
      queryParams.append('filter_status[]', status);
    });
  }

  if (params.include_auto_rejected !== undefined) {
    queryParams.set('include_auto_rejected', params.include_auto_rejected.toString());
  }

  // Include option
  if (params.include) {
    queryParams.set('include', params.include);
  }

  // Use standard apiGet utility - report endpoint is now under /api/v2
  return apiGet<GetApprovalReportResponse>(`approval-report?${queryParams.toString()}`);
};

